import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { BASE_URL } from '../services/api';

const QuickStatsBar = ({ stats = null, loading = false, error = null, className = '' }) => {
  const [localStats, setLocalStats] = useState(stats);
  const [localLoading, setLocalLoading] = useState(loading);
  const [localError, setLocalError] = useState(error);
  
  const API_BASE_URL = BASE_URL;

  const fetchQuickStats = useCallback(async () => {
    // If stats are provided via props, use them
    if (stats) {
      setLocalStats(stats);
      return;
    }
    
    try {
      setLocalLoading(true);
      setLocalError(null);
      
      const response = await fetch(`${API_BASE_URL}/quick_stats.php?limit=4`);
      const result = await response.json();
      
      if (result.success) {
        setLocalStats(result.data);
      } else {
        setLocalError('Failed to load statistics');
      }
    } catch (err) {
      console.error('Fetch quick stats error:', err);
      setLocalError('Unable to load statistics');
    } finally {
      setLocalLoading(false);
    }
  }, [stats, API_BASE_URL]);

  useEffect(() => {
    fetchQuickStats();
  }, [fetchQuickStats]);

  // Default fallback stats
  const defaultStats = [
    { title: 'Total Clients', value: '400+', color: 'primary', icon: 'fas fa-users' },
    { title: 'Total Services', value: '6+', color: 'warning', icon: 'fas fa-cogs' },
    { title: 'Total Projects', value: '6+', color: 'success', icon: 'fas fa-project-diagram' },
    { title: 'Years of Excellence', value: '5+', color: 'info', icon: 'fas fa-award' }
  ];

  const statsToDisplay = localStats || defaultStats;

  // Map Bootstrap color classes
  const getColorClass = (color) => {
    const colorMap = {
      'primary': 'bg-primary',
      'secondary': 'bg-secondary',
      'success': 'bg-success',
      'danger': 'bg-danger',
      'warning': 'bg-warning',
      'info': 'bg-info',
      'light': 'bg-light text-dark',
      'dark': 'bg-dark',
      'blue': 'bg-primary',
      'green': 'bg-success',
      'red': 'bg-danger',
      'yellow': 'bg-warning',
      'orange': 'bg-warning',
      'purple': 'bg-primary'
    };
    
    return colorMap[color] || 'bg-primary';
  };

  // Get text color class
  const getTextColorClass = (color) => {
    const lightColors = ['light', 'warning', 'info'];
    return lightColors.includes(color) ? 'text-dark' : 'text-white';
  };

  if (localLoading) {
    return (
      <section className={`py-4 bg-primary text-white ${className}`}>
        <Container>
          <Row className="text-center">
            {[1, 2, 3, 4].map((index) => (
              <Col md={3} key={index} className="mb-3 mb-md-0">
                <div className="stat-item">
                  <Spinner animation="border" size="sm" variant="light" />
                  <p className="mt-2 mb-0">Loading...</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    );
  }

  if (localError && !statsToDisplay) {
    return (
      <section className={`py-4 bg-primary text-white ${className}`}>
        <Container>
          <Alert variant="light" className="mb-0 text-center">
            <small>{localError}</small>
          </Alert>
        </Container>
      </section>
    );
  }

  return (
    <section className={`quick-stats-bar py-4 ${className}`} style={{ backgroundColor: '#1a1a2e' }}>
      <Container>
        <Row className="text-center justify-content-center">
          {statsToDisplay.map((stat, index) => (
            <Col 
              key={stat.id || index} 
              xs={6} 
              md={3} 
              className="mb-4 mb-md-0"
            >
              <Card 
                as={stat.link ? Link : 'div'} 
                to={stat.link || null}
                className={`stat-card border-0 shadow-sm h-100 ${stat.link ? 'clickable' : ''}`}
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <Card.Body className="py-3 px-2">
                  <div className="stat-icon mb-2">
                    <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${getColorClass(stat.color)}`}
                         style={{ width: '60px', height: '60px' }}>
                      <i className={`${stat.icon || 'fas fa-chart-line'} fa-lg text-white`}></i>
                    </div>
                  </div>
                  <Card.Title className="mb-1">
                    <h2 className={`fw-bold mb-1 ${getTextColorClass(stat.color)}`}>
                      {stat.value}
                    </h2>
                  </Card.Title>
                  <Card.Text className="mb-0 text-white opacity-75">
                    {stat.title}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
      
      <style jsx="true">{`
        .quick-stats-bar {
          position: relative;
          overflow: hidden;
        }
        
        .quick-stats-bar::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          z-index: -1;
        }
        
        .stat-card {
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.1) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
        }
        
        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          background: rgba(255, 255, 255, 0.15) !important;
        }
        
        .stat-card.clickable {
          cursor: pointer;
        }
        
        .stat-card.clickable:hover {
          text-decoration: none;
        }
        
        .stat-icon {
          transition: transform 0.3s ease;
        }
        
        .stat-card:hover .stat-icon {
          transform: scale(1.1);
        }
        
        @media (max-width: 768px) {
          .stat-card {
            margin-bottom: 15px;
          }
          
          .stat-icon div {
            width: 50px !important;
            height: 50px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default QuickStatsBar;